import { useId } from 'react';

import type { Locale } from '../i18n';

/**
 * Flaggen für die Sprachwahl — als Inline-SVG selbst gezeichnet.
 *
 * Nicht als Emoji: die Flaggen-Emoji fehlen auf manchen Systemen ganz oder erscheinen als
 * Buchstabenpaar. Nicht als Bilddatei: die App muss offline laufen und soll für drei Rechtecke
 * keine Dateien nachladen.
 *
 * Englisch bekommt den Union Jack — die gebräuchliche Vertretung der Sprache in solchen
 * Auswahlen, auch wenn eine Sprache streng genommen kein Land ist.
 *
 * ⚠️ **Alle drei im Verhältnis 3:2.** Richtig wären 3:5 (Deutschland), 2:3 (Frankreich) und
 * 1:2 (Vereinigtes Königreich) — drei verschiedene Formen nebeneinander lesen sich in einer
 * Liste aber als Fehler, nicht als Genauigkeit. Ein gemeinsames Format ist die bewusste Wahl.
 */

/**
 * Die vier Viertel des Union Jack und wohin das rote Band darin ausweicht.
 *
 * ⚠️ **Der Versatz ist der ganze Punkt** (Befund des Nutzers, 2026-09-22: die Flagge sah
 * „nicht nach UK" aus). Bis dahin lagen die roten Diagonalen mittig im weißen Band — das
 * ergibt ein symmetrisches Kreuz, das es so nicht gibt. Beim echten Union Jack ist das rote
 * Andreaskreuz **gegengleich versetzt**: oben am Mast liegt das breite Weiß über dem Rot,
 * fliegend darunter. Daran erkennt man, ob eine Flagge verkehrt herum hängt — eine Flagge
 * ohne diesen Versatz hat gar keine richtige Lage.
 *
 * `x`/`y` ist die linke obere Ecke des Viertels (für den Zuschnitt), `ecke` der Zipfel der
 * Flagge, aus dem das Band kommt, und `weg` die Richtung, in die es aus der Mitte des weißen
 * rückt — senkrecht zur Diagonale, um genau eine Einheit: das weiße Band ist 4 breit, das
 * rote 2, damit bleibt auf einer Seite genau 1 übrig.
 */
export const VIERTEL = [
  { name: 'ol', x: 0, y: 0, ecke: [0, 0], weg: [-2, 3] },
  { name: 'or', x: 15, y: 0, ecke: [30, 0], weg: [-2, -3] },
  { name: 'ul', x: 0, y: 10, ecke: [0, 20], weg: [2, 3] },
  { name: 'ur', x: 15, y: 10, ecke: [30, 20], weg: [2, -3] },
] as const;

export type Viertel = (typeof VIERTEL)[number];

/** Das rote Band eines Viertels: von der Ecke zur Mitte, senkrecht um 1 verschoben. */
export function rotesBand(v: Viertel): string {
  const einheit = (x: number, y: number): [number, number] => {
    const laenge = Math.hypot(x, y);
    return [x / laenge, y / laenge];
  };
  const [vx, vy] = einheit(15 - v.ecke[0], 10 - v.ecke[1]);
  const [wx, wy] = einheit(v.weg[0], v.weg[1]);
  /*
   * Über die Ecke hinaus verlängert: das verschobene Band verfehlte sie sonst um seinen
   * eigenen Versatz und liesse dort ein blaues Dreieck stehen. Der Rahmen schneidet ab.
   */
  const x1 = v.ecke[0] - vx * 4 + wx;
  const y1 = v.ecke[1] - vy * 4 + wy;
  return `M${x1.toFixed(3)},${y1.toFixed(3)} L${(15 + wx).toFixed(3)},${(10 + wy).toFixed(3)}`;
}

export function Flag({ locale, size = 22 }: { locale: Locale; size?: number }) {
  /*
   * Eigene Kennungen je Flagge: die `clipPath`-Verweise gelten im ganzen Dokument. Stünden
   * zwei Union Jacks auf einer Seite, nähme der zweite die Zuschnitte des ersten — und ein
   * fester Name wäre genau dann falsch, wenn es niemand erwartet.
   */
  const id = useId().replace(/:/g, '');
  const height = Math.round((size * 2) / 3);
  const common = {
    width: size,
    height,
    viewBox: '0 0 30 20',
    role: 'presentation' as const,
    'aria-hidden': true,
    style: { borderRadius: 3, display: 'block' as const },
  };

  if (locale === 'de') {
    return (
      <svg {...common}>
        <rect width="30" height="20" fill="#000000" />
        <rect y="6.667" width="30" height="6.667" fill="#DD0000" />
        <rect y="13.333" width="30" height="6.667" fill="#FFCE00" />
      </svg>
    );
  }

  if (locale === 'fr') {
    return (
      <svg {...common}>
        <rect width="30" height="20" fill="#FFFFFF" />
        <rect width="10" height="20" fill="#002395" />
        <rect x="20" width="10" height="20" fill="#ED2939" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <clipPath id={`${id}-rahmen`}>
        <rect width="30" height="20" rx="0" />
      </clipPath>
      {VIERTEL.map((v) => (
        <clipPath key={v.name} id={`${id}-${v.name}`}>
          <rect x={v.x} y={v.y} width="15" height="10" />
        </clipPath>
      ))}
      <g clipPath={`url(#${id}-rahmen)`}>
        <rect width="30" height="20" fill="#012169" />
        {/* Andreaskreuz: das breite weiße Band liegt durchgehend. */}
        <path d="M0,0 L30,20 M30,0 L0,20" stroke="#FFFFFF" strokeWidth="4" />
        {/* Das rote Kreuz des heiligen Patrick, Viertel für Viertel versetzt. */}
        {VIERTEL.map((v) => (
          <path
            key={v.name}
            d={rotesBand(v)}
            stroke="#C8102E"
            strokeWidth="2"
            clipPath={`url(#${id}-${v.name})`}
          />
        ))}
        {/* Georgskreuz */}
        <path d="M15,0 V20 M0,10 H30" stroke="#FFFFFF" strokeWidth="6" />
        <path d="M15,0 V20 M0,10 H30" stroke="#C8102E" strokeWidth="3.5" />
      </g>
    </svg>
  );
}
